import { useState } from "react"
import { mutate } from "swr"
import * as InboundOrderApi from "../../../apis/inbound-order"
import type { ICreateInboundInput } from "../../../apis/inbound-order"

// Revalidate without data: passing `undefined` as data clears the cache, which unmounts the
// drawer (loading early-return) and drops dialog state such as the stock-repost offer.
const invalidate = () =>
  mutate(
    (key) =>
      Array.isArray(key) &&
      (key[0] === "inbound-orders" ||
        key[0] === "inbound-order" ||
        key[0] === "inbound-exceptions"),
  )

export function useInboundMutations() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  async function wrap<T>(fn: () => Promise<T>): Promise<T> {
    setSubmitting(true)
    setError(null)
    try {
      const r = await fn()
      await invalidate()
      return r
    } catch (e) {
      // HttpHandler is not an Error: keep its message (an `error.*` key) instead of "[object Object]".
      const err = e instanceof Error ? e : new Error((e as { message?: string } | null)?.message ?? String(e))
      setError(err)
      throw err
    } finally {
      setSubmitting(false)
    }
  }

  return {
    submitting,
    error,
    create: (data: ICreateInboundInput) =>
      wrap(() => InboundOrderApi.create(data)),
    update: (id: string, patch: Partial<ICreateInboundInput>) =>
      wrap(() => InboundOrderApi.update(id, patch)),
    approve: (id: string) =>
      wrap(() => InboundOrderApi.approve(id)),
    dispatch: (id: string, body: { driverId: string; vehicleId?: string }) =>
      wrap(() => InboundOrderApi.dispatch(id, body)),
    cancel: (id: string, reason: string) =>
      wrap(() => InboundOrderApi.cancel(id, { reason })),
    pickupStart: (id: string, body: { pin: string; fromUserId?: string; toUserId?: string }) =>
      wrap(() => InboundOrderApi.pickupStart(id, body)),
    pickupComplete: (id: string, body: { pin: string; receivedItems: Array<{ itemId: string; sku?: string; productId?: string; quantity: number }> }) =>
      wrap(() => InboundOrderApi.pickupComplete(id, body)),
    arrive: (id: string, body?: { capturedLatitude?: number; capturedLongitude?: number }) =>
      wrap(() => InboundOrderApi.arrive(id, body ?? {})),
    receive: (id: string, body: { driverPin: string; receiverNotes?: string; items: Array<{ inboundOrderItemId: string; receivedQuantity: number; condition: string; photoUrls?: string[]; notes?: string }> }) =>
      wrap(() => InboundOrderApi.receive(id, body)),
    repostStock: (id: string) =>
      wrap(() => InboundOrderApi.repostStock(id)),
  }
}
